import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bug, Lightbulb, X, ImagePlus, CheckCircle } from 'lucide-react';
import api from '../services/api';
import { OPEN_BUG_REPORT, recentErrors } from '../utils/errorCollector';

const field = { padding: '8px 10px', border: '1px solid var(--input-border, var(--border))', borderRadius: 6, fontSize: 14, background: 'var(--input-bg, var(--surface))', color: 'var(--text-main)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const ghost = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text-main)', cursor: 'pointer', fontSize: 13 };
const muted = { color: 'var(--text-muted)', fontSize: 13 };
const SEVERITIES = [['LOW', 'Low: cosmetic or minor'], ['MEDIUM', 'Medium: annoying, has a workaround'], ['HIGH', 'High: blocks my work'], ['CRITICAL', 'Critical: data wrong or lost']];
const MAX_BYTES = 5 * 1024 * 1024;
const blank = (type = 'BUG') => ({ type, title: '', description: '', stepsToReproduce: '', severity: 'MEDIUM' });

// Report a bug or a suggestion from any page. Mounted once in the signed-in
// layout and opened with openBugReport() (Help menu, Dashboard).
const BugReportModal = () => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank());
    const [shot, setShot] = useState(null); // { file, url }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(null);
    const page = useRef('/');

    useEffect(() => {
        const onOpen = (e) => {
            page.current = window.location.pathname; // the page the person was on
            setForm({ ...blank(e.detail?.type), title: e.detail?.title || '' });
            setShot(null); setError(null); setSent(null); setOpen(true);
        };
        window.addEventListener(OPEN_BUG_REPORT, onOpen);
        return () => window.removeEventListener(OPEN_BUG_REPORT, onOpen);
    }, []);

    useEffect(() => () => { if (shot?.url) URL.revokeObjectURL(shot.url); }, [shot]);

    const close = () => setOpen(false);
    useEffect(() => { setOpen(false); }, [location.pathname]); // navigating away closes it

    const takeFile = (file) => {
        if (!file) return;
        if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) { setError('The screenshot must be a PNG, JPEG, WebP or GIF image'); return; }
        if (file.size > MAX_BYTES) { setError('The screenshot must be under 5 MB'); return; }
        setError(null);
        setShot({ file, url: URL.createObjectURL(file) });
    };
    const onPaste = (e) => {
        const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
        if (item) { e.preventDefault(); takeFile(item.getAsFile()); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => { if (k !== 'severity' || form.type === 'BUG') fd.append(k, v); });
            fd.append('pagePath', page.current);
            fd.append('context', JSON.stringify({
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                screen: `${window.screen.width}x${window.screen.height}`,
                language: navigator.language,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                theme: document.documentElement.getAttribute('data-theme') || undefined,
                online: navigator.onLine,
                appBuild: import.meta.env.VITE_APP_VERSION || undefined,
                recentErrors: recentErrors(),
            }));
            if (shot) fd.append('screenshot', shot.file);
            const res = await api.post('/bugs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSent(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not send the report. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    const isBug = form.type === 'BUG';
    return (
        <div role="dialog" aria-label="Report a bug or suggestion" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={close}>
            <div className="card" onClick={e => e.stopPropagation()} onPaste={onPaste} style={{ width: 600, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', padding: 24, background: 'var(--surface)', display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, flex: 1 }}>{sent ? 'Thank you' : 'Report a bug or suggestion'}</h3>
                    <button aria-label="Close" onClick={close} style={{ ...ghost, border: 'none' }}><X size={16} /></button>
                </div>

                {sent ? (
                    <>
                        <p style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}><CheckCircle size={18} color="#10b981" /> Sent as <strong>{sent.ref}</strong>. We emailed you a copy.</p>
                        {JSON.parse(localStorage.getItem('user') || '{}').status !== 'PRE_JOINING' && <p style={{ ...muted, margin: 0 }}>You can follow it under <Link to="/bugs" onClick={close}>My reports</Link>.</p>}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 14 }} onClick={close}>Done</button></div>
                    </>
                ) : (
                    <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label="Type">
                            {[['BUG', 'Something is wrong', Bug], ['SUGGESTION', 'Suggestion', Lightbulb]].map(([v, l, Icon]) => (
                                <button key={v} type="button" role="radio" aria-checked={form.type === v} onClick={() => setForm({ ...form, type: v })}
                                    style={{ ...ghost, flex: 1, justifyContent: 'center', padding: '10px', borderColor: form.type === v ? 'var(--primary)' : 'var(--border)', background: form.type === v ? 'var(--accent-subtle)' : 'transparent', fontWeight: form.type === v ? 600 : 400 }}>
                                    <Icon size={15} /> {l}
                                </button>
                            ))}
                        </div>
                        <label style={{ display: 'grid', gap: 4 }}>Short title
                            <input aria-label="Title" style={field} maxLength={200} autoFocus value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                                placeholder={isBug ? 'e.g. Payslip shows the wrong month' : 'e.g. Let me export leave history as PDF'} />
                        </label>
                        {isBug && (
                            <label style={{ display: 'grid', gap: 4 }}>How bad is it?
                                <select aria-label="Severity" style={field} value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                                    {SEVERITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </label>
                        )}
                        <label style={{ display: 'grid', gap: 4 }}>{isBug ? 'What happened? What did you expect?' : 'What would you like, and why?'}
                            <textarea aria-label="Description" rows={4} style={{ ...field, resize: 'vertical' }} maxLength={10000} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </label>
                        {isBug && (
                            <label style={{ display: 'grid', gap: 4 }}>Steps to reproduce <span style={muted}>(optional)</span>
                                <textarea aria-label="Steps to reproduce" rows={3} style={{ ...field, resize: 'vertical' }} maxLength={5000} value={form.stepsToReproduce} onChange={e => setForm({ ...form, stepsToReproduce: e.target.value })}
                                    placeholder={'1. Open My Salary\n2. Click View Slip for October\n3. …'} />
                            </label>
                        )}
                        <div style={{ display: 'grid', gap: 6 }}>
                            <span>Screenshot <span style={muted}>(optional: choose a file or paste with Ctrl+V)</span></span>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                <label style={{ ...ghost, cursor: 'pointer' }}><ImagePlus size={14} /> Choose image
                                    <input aria-label="Screenshot" type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={e => takeFile(e.target.files?.[0])} />
                                </label>
                                {shot && (
                                    <>
                                        <img src={shot.url} alt="Screenshot preview" style={{ maxHeight: 80, maxWidth: 160, borderRadius: 4, border: '1px solid var(--border)' }} />
                                        <button type="button" style={{ ...ghost, border: 'none' }} onClick={() => setShot(null)}>Remove</button>
                                    </>
                                )}
                            </div>
                        </div>
                        <p style={{ ...muted, margin: 0 }}>We also send the page you were on ({page.current}), your browser and screen size, and any recent errors on this page, to help us find the problem.</p>
                        {error && <div role="alert" style={{ color: '#ef4444' }}>{error}</div>}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" style={ghost} onClick={close}>Cancel</button>
                            <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 14 }} disabled={busy || form.title.trim().length < 5 || form.description.trim().length < 10}>
                                {busy ? 'Sending…' : 'Send'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BugReportModal;
